import {
  Upload,
  Search,
  Sparkles,
  FileSignature,
  FileText,
  Receipt,
  Calendar,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
  Badge,
} from "../components/ui";
import { useSeo } from "../lib/seo";

const FLOW = [
  {
    icon: Upload,
    title: "Chart ingest",
    body: "Drop chart files (PDF/CSV/HL7). Multipart upload, per-tenant Storage prefix. No PHI ever leaves your tenant boundary.",
  },
  {
    icon: Search,
    title: "PHI de-identification",
    body: "Safe Harbor 18-identifier redaction before any LLM call. Per-tenant key vault. Audit log on every de-id event.",
  },
  {
    icon: Sparkles,
    title: "Clinical engine + AI synthesis",
    body: "Rule engine fires evidence-cited flags. Claude Sonnet 4.6 synthesizes recommendations with learning loop tuning.",
  },
  {
    icon: FileText,
    title: "Bundle render",
    body: "React-PDF assembles SBAR-formatted recommendation bundle with citations + facility-level summary.",
  },
  {
    icon: FileSignature,
    title: "Adobe Sign workflow",
    body: "Embedded signing for medical director sign-off. Webhook auto-flips status. Append-only audit on every transition.",
  },
  {
    icon: Receipt,
    title: "QBO invoice draft",
    body: "Auto-draft invoice from time entries + bundle outcomes. Tenant-mapped item refs. Race-guarded line item linking.",
  },
  {
    icon: Calendar,
    title: "Calendar + reminders",
    body: "Two-way Google + Outlook sync. License renewal sweep. Resend + Twilio delivery on opt-in.",
  },
];

export function Platform() {
  useSeo({
    title: "Platform — WeConsult Rx",
    description:
      "End-to-end consultant pharmacy workflow: chart ingest, PHI de-identification, clinical engine, AI synthesis, bundle render, Adobe Sign, QBO invoicing, and calendar sync.",
    path: "/platform",
  });

  return (
    <>
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Platform</Eyebrow>
            <Heading level={1} className="mt-3">
              One workflow, end to end.
            </Heading>
            <Lede className="mt-6">
              From chart ingest to signed recommendation bundle to invoiced
              billable hour &mdash; with PHI safety enforced in code at every
              step.
            </Lede>
            <div className="mt-8 flex gap-3">
              <LinkButton to="/demo">Request a demo</LinkButton>
              <LinkButton to="/features" variant="secondary">
                Feature deep-dive
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      {/* The flow */}
      <Section className="pt-8" tone="soft">
        <Container>
          <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-label="Workflow steps">
            {FLOW.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card as="li" key={f.title}>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-tagline text-forest-700 font-semibold">
                      Step {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="mt-3 flex h-11 w-11 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <Heading level={3} className="mt-4">
                    {f.title}
                  </Heading>
                  <p className="mt-2 text-ink-600 text-sm">{f.body}</p>
                </Card>
              );
            })}
          </ol>
        </Container>
      </Section>

      {/* Architecture */}
      <Section>
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Eyebrow>Under the hood</Eyebrow>
              <Heading level={2} className="mt-2">
                A modern Supabase + serverless stack.
              </Heading>
              <p className="mt-4 text-ink-600">
                Multi-tenant Postgres with row-level security on every PHI table.
                Background workers via pgmq. Edge functions for low-latency
                lookups. AWS Bedrock for AI inference inside a HIPAA-eligible
                region.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-ink-800">
                {[
                  "Postgres 15 + RLS pen-tested on every commit",
                  "AWS Bedrock (Claude Sonnet 4.6) — HIPAA-eligible region",
                  "Adobe Sign embedded signing with HMAC webhook verification",
                  "Resend + Twilio for opt-in reminders, Vault for keys",
                  "QuickBooks Online OAuth, race-guarded invoice drafting",
                  "Append-only audit log, 7-year retention envelope",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-forest-700 mt-0.5" aria-hidden />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="bg-forest-gradient text-white border-transparent">
              <Badge tone="neutral" className="bg-white/15 border-white/20 text-white">
                System diagram
              </Badge>
              <div className="mt-6 grid gap-3 text-sm">
                {[
                  ["Tenant browser", "PWA + landing"],
                  ["Edge / API", "Fastify · JWT · Tenant scope"],
                  ["Workers", "pgmq · Bedrock · Adobe · QBO"],
                  ["Database", "Postgres 15 · RLS · pgmq · pg_vector"],
                  ["Storage", "Supabase Storage · per-tenant prefix"],
                  ["Audit", "Append-only log · system actor"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white/10 px-4 py-3"
                  >
                    <span className="font-semibold">{k}</span>
                    <span className="text-white/85">{v}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div className="mt-12 text-center">
            <LinkButton to="/security" variant="secondary">
              Security overview
              <ArrowRight className="h-4 w-4" aria-hidden />
            </LinkButton>
          </div>
        </Container>
      </Section>
    </>
  );
}
